import { cn } from '@/shadcn-ui/utils';
import { colors } from '@/theme/colors';
import { DrawerDialog, DrawerDialogProps } from '@/common/components/drawer-dialog';
import { Form, useSubmit } from 'react-router';
import { RadioGroup, RadioGroupItem } from '@radix-ui/react-radio-group';
import Calendar from '@/icons/calendar';
import FilterPill from './filter-pill';
import getPlanNameFromMiles from '@/account/utils/get-plan-name-from-miles';
import Spacer from '@/common/components/spacer';
import StatusBanner from '@/common/components/status-banner';
import { useEffect } from 'react';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';

interface FilterPlanDrawerDialogProps extends DrawerDialogProps {
  mileagePlans: number[];
  selectedPlanMileage?: number;
  handleOpen: () => void;
  fullstoryEventDelegate: (mileage: string) => void;
  error?: string;
  pillClassName?: string;
  dark?: boolean;
  showShorthandCopy?: boolean;
}

export default function FilterPlanDrawerDialog({
  mileagePlans,
  selectedPlanMileage = 850,
  handleClose,
  error,
  open,
  handleOpen,
  fullstoryEventDelegate,
  pillClassName,
  dark,
  showShorthandCopy,
}: FilterPlanDrawerDialogProps) {
  const planName = getPlanNameFromMiles(selectedPlanMileage);
  const submit = useSubmit();

  useEffect(() => {
    if (open) {
      fullstoryEvent('monthly_plan_open');
    }
  }, [open]);

  return (
    <>
      <FilterPill
        onClick={handleOpen}
        icon={<Calendar fillPrimary={dark ? colors.neutral100 : colors.neutral1} />}
        text={planName}
        className={pillClassName}
        dark={dark}
        showShorthandCopy={showShorthandCopy}
      />

      <DrawerDialog open={open} handleClose={handleClose} title="Monthly mileage">
        {error ? (
          <>
            <StatusBanner
              variant="error"
              message={{
                text: error,
              }}
              shouldScrollToTop={true}
            />
            <Spacer variant="height16" />
          </>
        ) : null}
        <Form>
          <p className="body2">
            Select a plan to preview your rates. You can change it anytime!
          </p>
          <Spacer variant="height4" />
          <RadioGroup
            name="mileage"
            onValueChange={(event) => {
              // useEffect handles modal close
              submit(
                {
                  intent: 'add_plan_mileage',
                  plan_mileage: event,
                },
                { method: 'post' }
              );
              fullstoryEventDelegate(event);
            }}
            className="grid gap-4"
          >
            {mileagePlans.map((mileage) => (
              <MileagePlanRadioItem
                key={mileage}
                mileage={mileage}
                isSelected={selectedPlanMileage === mileage}
              />
            ))}
          </RadioGroup>
        </Form>
      </DrawerDialog>
    </>
  );
}

interface MileagePlanRadioItemProps {
  mileage: number;
  isSelected: boolean;
}

function MileagePlanRadioItem({ mileage, isSelected }: MileagePlanRadioItemProps) {
  const planName = getPlanNameFromMiles(mileage);

  const getDescription = (mileage: number) =>
    mileage === 0 ? 'Mileage charged separately' : `${mileage} miles included`;

  return (
    <label
      key={mileage}
      htmlFor={`${mileage}-radio`}
      className={cn(
        'group flex cursor-pointer flex-col gap-2 rounded-md p-4 ring-inset hover:text-flexcar-blue30',
        {
          'ring-1.5 ring-flexcar-neutral80': !isSelected,
          'shadow-boxSelected ring-2 ring-flexcar-blue30': isSelected,
        }
      )}
    >
      <div className="flex flex-row justify-between">
        <div className="flex flex-row items-center justify-center gap-3">
          <RadioGroupItem
            value={`${mileage}`}
            id={`${mileage}-radio`}
            className={`hidden`}
          />

          <p
            className={cn('body-strong group-hover:text-flexcar-blue30', {
              'text-flexcar-blue30': isSelected,
            })}
          >
            {planName}
          </p>
        </div>

        <p
          className={cn('body group-hover:text-flexcar-blue30', {
            'text-flexcar-blue30': isSelected,
          })}
        >
          {getDescription(mileage)}
        </p>
      </div>
    </label>
  );
}
